import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    IconButton,
    CircularProgress,
    useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useIndexedDB } from "react-indexed-db-hook";
import { enqueueSnackbar } from "notistack";
import { loadConflictsFromStorage, ConflictItem } from "utils/dataConflictDetector";
import { flagBagApi } from "services/api/flagbags";
import { useData } from "store/DataProvider";
import useFlagBagController from "hooks/useFlagBagController";
import { useAppStatus } from "hooks/useAppStatus";
import { StatusLabel, StatusCode } from "types/flagbag";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";
import { formatDateTime } from "services/dateHelper";
import { normalizeFlagbagServerStatus } from "utils/flagbagStatusUtils";

function normalizeStatusValue(raw: unknown): string {
    if (raw === null || raw === undefined) return "";
    return String(raw).trim().toUpperCase().replace(/\s+/g, "_");
}

/** Match flagbag-list-columns: raw / normalized key → StatusLabel; keep sync-error and already-resolved labels. */
function resolveConflictStatusLabel(raw: string | undefined | null): string {
    console.log(raw);
    if (raw == null || raw === "") return "-";
    if (raw === "-" || raw === "同步失敗") return raw;
    const direct = StatusLabel[raw as StatusCode];
    if (direct) return direct;
    console.log(raw, direct);
    const normalized = normalizeStatusValue(raw);
    if (!normalized) return "-";
    const lookupKey = normalized.toLowerCase() as StatusCode;
    console.log(lookupKey, StatusLabel[lookupKey]);
    return StatusLabel[lookupKey] || raw;
}

interface Props {
    open: boolean;
    isOnline: boolean;
    flagBagId: string;
    flagbagNum: string;
    serverFlagbagStatus: string;
    serverUpdateTime: string;
    serverUpdateBy: string;
    localFlagbagStatus: string;
    onClose?: () => void;
    onSuccess?: () => void; // 成功后的回调，用于刷新列表
}

/**
 * 数据选择对话框
 * 当检测到本地数据与服务器数据冲突时，让用户选择保留哪个版本
 */
export const ConflictSelectModals: React.FC<Props> = ({
    open,
    isOnline,
    flagBagId,
    flagbagNum,
    serverFlagbagStatus,
    serverUpdateTime,
    serverUpdateBy,
    localFlagbagStatus,
    onClose,
    onSuccess,
}) => {
    const [selectedValue, setSelectedValue] = useState<"server" | "local">("server");
    const [isLoading, setIsLoading] = useState(false);
    const conflicts = loadConflictsFromStorage() || [];
    const conflict = conflicts.find((c: ConflictItem) => c.flagBagId === flagBagId) || null;
    const flagBagDB = initIndexedDbIfMissingStore("flagbag");
    const offlineDB = initIndexedDbIfMissingStore("offline");
    const data = useData();
    const theme = useTheme();
    const FlagBagController = useFlagBagController();
    const { refreshAppStatus } = useAppStatus();

    // 当对话框打开时，重置选择为服务器数据
    React.useEffect(() => {
        if (open) {
            setSelectedValue("server");
        }
    }, [open]);

    const handleClose = () => {
        if (!isLoading) {
            onClose?.();
        }
    };

    const clearOfflineRecordsForFlagbag = async () => {
        const allOfflineRecords = await offlineDB.getAll();
        const recordsToDelete = allOfflineRecords.filter(
            (record: any) => record.flagBagID === flagBagId
        );
        for (const record of recordsToDelete) {
            await offlineDB.deleteRecord(record.id);
        }
    };

    // 采用服务器数据
    const adoptServerData = async (conflict: ConflictItem) => {
        const flagBagData = await flagBagDB.getByID(flagBagId);
        if (flagBagData) {
            const serverStatus = normalizeFlagbagServerStatus(conflict.serverStatus);
            await flagBagDB.update({
                ...flagBagData,
                status: serverStatus,
                currentStatus: serverStatus,
            });
        }

        await clearOfflineRecordsForFlagbag();

        enqueueSnackbar(`已採用伺服器數據：${flagbagNum}`, { variant: "success" });
    };

    // 采用本地数据
    const adoptLocalData = async (conflict: ConflictItem) => {
        const eventId = data.getEventId();
        const stationId = data.getStationId();

        if (!eventId || !stationId) {
            throw new Error("缺少活動或站點資訊");
        }

        // 获取 API 状态和必要参数
        const { apiStatus, finalStationId, volunteerId } = await prepareUpdatePayload(conflict, stationId);

        // 调用 API 更新服务器
        await flagBagApi.updateSingle(flagBagId, eventId, stationId, {
            status: apiStatus,
            ...(finalStationId && { finalStationId }),
            ...(volunteerId && { volunteerId }),
        });

        // 更新本地状态
        await updateLocalFlagBagStatus(apiStatus, conflict);

        await clearOfflineRecordsForFlagbag();

        enqueueSnackbar(`已採用本地數據並同步至伺服器：${flagbagNum}`, { variant: "success" });
    };

    // 准备更新请求的参数
    const prepareUpdatePayload = async (conflict: ConflictItem, stationId: string) => {
        // 转换状态
        const apiStatus = convertToApiStatus(conflict.localStatus);

        // 获取 finalStationId 和 volunteerId
        let finalStationId: string | undefined;
        let volunteerId: string | undefined;

        // 从 offline 数据库获取
        const allOfflineRecords = await offlineDB.getAll();
        const latestOfflineRecord = allOfflineRecords
            .filter((record: any) => record.flagBagID === flagBagId)
            .sort((a: any, b: any) => {
                const timeA = parseInt(a.updatedAt || "0");
                const timeB = parseInt(b.updatedAt || "0");
                return timeB - timeA;
            })[0];

        if (latestOfflineRecord) {
            finalStationId = latestOfflineRecord.finalStationId;
            volunteerId = latestOfflineRecord.volunteerId;
        }

        // 从 flagBag 数据库获取（如果还没有）
        if (!finalStationId || !volunteerId) {
            const flagBagData = await flagBagDB.getByID(flagBagId);
            if (flagBagData) {
                finalStationId = finalStationId || flagBagData.finalStationId;
                volunteerId = volunteerId || flagBagData.volunteerId;
            }
        }

        // 收集状态需要 finalStationId
        const isCollectedStatus = ["collected", "collected_sub_to_bank", "collected_empty", "collected_other"].includes(apiStatus);
        if (isCollectedStatus) {
            finalStationId = finalStationId || stationId;
            if (!finalStationId) {
                throw new Error(`收集狀態 ${apiStatus} 需要提供 finalStationId`);
            }
        }

        return { apiStatus, finalStationId, volunteerId };
    };

    // 转换状态码为 API 格式
    const convertToApiStatus = (status: string): StatusCode => {
        return normalizeFlagbagServerStatus(status);
    };

    // 更新本地旗袋状态
    const updateLocalFlagBagStatus = async (apiStatus: string, conflict: ConflictItem) => {
        const flagBagData = await flagBagDB.getByID(flagBagId);
        if (!flagBagData) return;

        const statusCodeToUpdate = normalizeFlagbagServerStatus(conflict.localStatus || apiStatus);

        await flagBagDB.update({
            ...flagBagData,
            status: statusCodeToUpdate,
            currentStatus: statusCodeToUpdate,
        });
    };

    const handleConfirm = async () => {
        if (isLoading) return;

        try {
            setIsLoading(true);

            const conflicts = loadConflictsFromStorage() || [];
            const conflictIndex = conflicts.findIndex((c: ConflictItem) => c.flagBagId === flagBagId);

            if (conflictIndex === -1) {
                enqueueSnackbar("未找到衝突記錄", { variant: "warning" });
                setIsLoading(false);
                onClose?.();
                return;
            }

            const conflict = conflicts[conflictIndex];

            // 根据用户选择执行操作
            try {
                if (selectedValue === "server") {
                    await adoptServerData(conflict);
                } else {
                    await adoptLocalData(conflict);
                }
            } catch (error: any) {
                handleUpdateError(error, conflict);
                setIsLoading(false);
                return;
            }

            // 更新冲突列表
            conflicts.splice(conflictIndex, 1);
            if (conflicts.length > 0) {
                localStorage.setItem("dataConflicts", JSON.stringify(conflicts));
            } else {
                localStorage.removeItem("dataConflicts");
                localStorage.removeItem("dataConflictsTimestamp");
            }

            // 刷新数据
            try {
                await FlagBagController.initDB(data.getStationId());
                setTimeout(() => refreshAppStatus(), 200);
            } catch (error) {
                console.error("Failed to refresh data:", error);
            }

            // 关闭对话框并触发刷新
            setIsLoading(false);
            onClose?.();
            onSuccess?.();
        } catch (error: any) {
            console.error("Error handling conflict resolution:", error);
            enqueueSnackbar(
                error.message?.includes("數據庫") || error.message?.includes("IndexedDB")
                    ? "本地數據庫操作失敗，請重試"
                    : `處理衝突失敗：${error.message || "未知錯誤"}`,
                { variant: "error", autoHideDuration: 5000 }
            );
            setIsLoading(false);
        }
    };

    // 处理更新错误
    const handleUpdateError = (error: any, conflict: ConflictItem) => {
        let errorMessage = "更新伺服器狀態失敗";

        if (error.message) {
            if (error.message.includes("finalStationId")) {
                errorMessage = "缺少收集站點資訊，無法更新狀態";
            } else if (error.message.includes("無法識別的狀態") || error.message.includes("無法轉換狀態碼")) {
                errorMessage = `狀態格式錯誤：${error.message}`;
            } else if (error.message.includes("缺少活動或站點資訊")) {
                errorMessage = "缺少必要的活動或站點資訊，請重新登入";
            } else {
                errorMessage = `${errorMessage}：${error.message}`;
            }
        } else if (error.errorMsg) {
            if (error.errorMsg.includes("finalStationId") || error.errorMsg.includes("Final station")) {
                errorMessage = "伺服器要求提供收集站點資訊（finalStationId），請聯繫技術支持";
            } else if (error.errorMsg.includes("validation error") || error.errorMsg.includes("enum")) {
                errorMessage = "狀態值驗證失敗，伺服器不接受該狀態值";
            } else {
                errorMessage = `${errorMessage}：${error.errorMsg}`;
            }
        }

        enqueueSnackbar(errorMessage, { variant: "error", autoHideDuration: 6000 });
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedValue(event.target.value as "server" | "local");
    };

    return <>

    {isOnline ? (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "20px",
                    p: 2,
                },
            }}
        >
            {/* 标题栏 */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="h6" fontWeight="bold">
                    數據選擇
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <Close />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 0 }}>
                {/* 旗袋编号与冲突信息 */}
                <Box
                    sx={{
                        backgroundColor: "#FFF3E0",
                        borderRadius: "12px",
                        p: 2.5,
                        mb: 2,
                        border: "1px solid #FFB74D",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{ color: "#E65100" }}
                        >
                            旗袋編號：{flagbagNum}
                        </Typography>
                    </Box>
                    {conflict?.reason && (
                        <Box
                            sx={{
                                mt: 1.5,
                                pt: 1.5,
                                borderTop: "1px dashed #FFB74D",
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#F57C00",
                                    fontWeight: 500,
                                    mb: 0.5,
                                }}
                            >
                                衝突原因
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6D4C41" }}>
                                {conflict.reason}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* 选项列表 */}
                <Typography
                    variant="body2"
                    fontWeight="600"
                    sx={{ mb: 2, color: "#424242" }}
                >
                    請選擇要保留的數據版本
                </Typography>
                <RadioGroup value={selectedValue} onChange={handleChange}>
                    {/* 服务器数据选项 */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            mb: 2,
                            cursor: "pointer",
                            p: 2,
                            borderRadius: "12px",
                            border: "2px solid",
                            borderColor: selectedValue === "server" ? "#1976d2" : "#E0E0E0",
                            backgroundColor: selectedValue === "server" ? "#E3F2FD" : "transparent",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                borderColor: "#1976d2",
                                backgroundColor: "#F5F5F5",
                            },
                        }}
                        onClick={() => setSelectedValue("server")}
                    >
                        <Radio
                            value="server"
                            sx={{
                                color: "#1976d2",
                                "&.Mui-checked": {
                                    color: "#1976d2",
                                },
                                mt: -0.5,
                            }}
                        />
                        <Box flex={1}>
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                color="#1976d2"
                                mb={0.5}
                            >
                                伺服器數據
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: "#424242", fontWeight: 500 }}>
                                    狀態：{resolveConflictStatusLabel(serverFlagbagStatus)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                                    更新時間：{formatDateTime(serverUpdateTime)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* 本地数据选项 */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            p: 2,
                            borderRadius: "12px",
                            border: "2px solid",
                            borderColor: selectedValue === "local" ? "#1976d2" : "#E0E0E0",
                            backgroundColor: selectedValue === "local" ? "#E3F2FD" : "transparent",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                borderColor: "#1976d2",
                                backgroundColor: "#F5F5F5",
                            },
                        }}
                        onClick={() => setSelectedValue("local")}
                    >
                        <Radio
                            value="local"
                            sx={{
                                color: "text.secondary",
                                "&.Mui-checked": {
                                    color: "#1976d2",
                                },
                            }}
                        />
                        <Box flex={1}>
                            <Typography variant="body1" fontWeight="bold" color="#424242" mb={0.5}>
                                本地數據
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#424242", fontWeight: 500 }}>
                                狀態：{resolveConflictStatusLabel(localFlagbagStatus)}
                            </Typography>
                        </Box>
                    </Box>
                </RadioGroup>
            </DialogContent>

            {/* 确认按钮 */}
            <DialogActions sx={{ p: 0, mt: 3 }}>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                    sx={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        textTransform: "none",
                        fontSize: "16px",
                        fontWeight: "bold",
                        py: 1.5,
                        borderRadius: "12px",
                        "&:hover": {
                            backgroundColor: "#1565c0",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#ccc",
                            color: "white",
                        },
                    }}
                >
                    {isLoading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={20} sx={{ color: "white" }} />
                            <span>處理中...</span>
                        </Box>
                    ) : (
                        "確認"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    ): (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    p: "18px",
                }
            }}
        >
            <Box sx={{ fontSize: "14px", fontWeight: "500", display: "flex", flexDirection: "column", gap: "16px" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ color: theme.palette.online_status.offline }}>
                        無法修改該旗袋狀態
                    </Typography>
                    <Close onClick={onClose} />
                </Box>
                <Typography sx={{ color: "#000" }}>
                    離線模式下無法修改待處理旗袋的狀態，請恢復在線後重試。
                </Typography>
            </Box>
        </Dialog>
    )}
    </>
};
